import { getPur, pageParams, PurClientConfig, readBoolean, readDecimal, readId, readObject, readPage, readText, requireId } from "./purRead";

export type PurLookupKind = "projects" | "vendors" | "products" | "units" | "storages";

export interface PurLookupOption {
  id: number;
  label: string;
  code: string;
  unitId: number | null;
  unitName: string;
  hasLots: boolean | null;
  cost: string | null;
  salePrice: string | null;
  productType: string;
}

const paths: Record<PurLookupKind, string> = {
  projects: "projects/", vendors: "vendors/", products: "product/", units: "unit/", storages: "storage/",
};

// MeERP buys drugs and medical supplies only; core ProductList supports
// p_type__code__in so lab/service/package rows never enter the picker.
const PRODUCT_TYPE_CODES = ["DRUG", "SUPPLY"];

export const normalizePurLookup = (kind: PurLookupKind, value: unknown): PurLookupOption => {
  const row = readObject(value);
  const id = requireId(row.id);
  const code = readText(kind === "projects" ? row.number : row.code);
  const name = readText(kind === "projects" ? row.title : row.name);
  return {
    id, code, label: [code, name].filter(Boolean).join(" · ") || `#${id}`,
    unitId: kind === "products" ? readId(row.unit) : null,
    unitName: kind === "products" ? readText(row.unit_name) : "",
    hasLots: kind === "products" ? readBoolean(row.has_lots) : null,
    cost: kind === "products" ? readDecimal(row.overall_cost) : null,
    salePrice: kind === "products" ? readDecimal(row.price_normal) : null,
    productType: kind === "products" ? readText(row.p_type_name) : "",
  };
};

export const listPurLookup = async (
  client: PurClientConfig, kind: PurLookupKind, query: { limit: number; offset: number; search: string },
): Promise<{ count: number; records: PurLookupOption[] }> => {
  if (!client.apiToken || readId(client.divisionId) === null) throw new Error("Invalid PUR lookup context");
  const params = pageParams(query.limit, query.offset);
  // VendorViewSet search_fields currently reference VendorDetail fields. Do not
  // send its broken search query; the picker explicitly filters the loaded page.
  if (query.search.trim() && kind !== "vendors") params.set("search", query.search.trim());
  if (kind === "projects") params.set("division", String(client.divisionId));
  if (kind === "products") params.set("p_type__code__in", PRODUCT_TYPE_CODES.join(","));
  const core = kind === "products" || kind === "units" || kind === "storages";
  const data = await getPur(core ? { ...client, pathPrefix: "/apis/core" } : client, `${paths[kind]}?${params}`);
  return readPage(data, (row) => normalizePurLookup(kind, row));
};

export const getPurReceiptProduct = async (
  client: PurClientConfig, productId: number, productName: string,
): Promise<PurLookupOption & { hasLots: boolean }> => {
  if (!client.apiToken || readId(client.divisionId) === null || readId(productId) === null || !productName.trim()) {
    throw new Error("Invalid receipt product lookup context");
  }
  // ProductList has an exact name filter, but no ID filter/detail endpoint.
  // Always match the ID too: two products may share the same name.
  const core = { ...client, pathPrefix: "/apis/core" };
  for (let offset = 0; offset < 500; offset += 100) {
    const params = pageParams(100, offset);
    params.set("name", productName);
    const page = readPage(await getPur(core, `product/?${params}`), (row) => normalizePurLookup("products", row));
    const product = page.records.find((row) => row.id === productId);
    if (product) {
      if (product.hasLots === null) throw new Error("Receipt product has no has_lots flag");
      return { ...product, hasLots: product.hasLots };
    }
    if (offset + 100 >= page.count) break;
  }
  throw new Error("Receipt product not found in master data");
};
